import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { projectApi } from "../api/projects";
import type { ProjectParticipantRole } from "../api/projects";
import { invalidateDashboard } from "./dashboard";
import { requireData } from "./http";

export const projectQueryKeys = {
  all: ["projects"] as const,
  list: ["projects", "list", "newest"] as const,
  participants: (projectId: string) =>
    ["projects", projectId, "participants"] as const,
};

/** The most `GET /api/projects` returns; it has no paging yet. */
export const PROJECT_LIST_LIMIT = 100;

/**
 * The projects the caller may see, as Rust scopes them, newest first. Asking
 * for newest first means the row cap drops the oldest projects rather than
 * the most recent ones.
 */
export const useProjectList = () =>
  useQuery({
    queryKey: projectQueryKeys.list,
    queryFn: () =>
      projectApi
        .list({ order: "id", ascending: false, limit: PROJECT_LIST_LIMIT })
        .then(requireData),
  });

export const useProjectParticipants = (projectId: string, enabled = true) =>
  useQuery({
    queryKey: projectQueryKeys.participants(projectId),
    queryFn: ({ signal }) =>
      projectApi.listParticipants(projectId, signal).then(requireData),
    enabled: enabled && Boolean(projectId),
    staleTime: 30 * 1000,
  });

export const useAssignProjectParticipant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      userId,
      participantRole,
    }: {
      projectId: string;
      userId: string;
      participantRole: ProjectParticipantRole;
    }) =>
      projectApi
        .assignParticipant(projectId, userId, participantRole)
        .then(requireData),
    onSettled: (_data, _error, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: projectQueryKeys.participants(variables.projectId),
        }),
        invalidateDashboard(queryClient),
      ]),
  });
};

export const useEndProjectParticipant = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      participantId,
    }: {
      projectId: string;
      participantId: string;
    }) => projectApi.endParticipant(projectId, participantId).then(requireData),
    onSettled: (_data, _error, variables) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: projectQueryKeys.participants(variables.projectId),
        }),
        invalidateDashboard(queryClient),
      ]),
  });
};
